<template>
  <div class="w-full p-4">
    <div ref="topStickyRef" class="sticky top-14 z-30 bg-gray-200 -mx-4 px-4 pt-3 pb-1">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">Accounting Queue</h2>
    </div>
    <!-- Tabs -->
    <div class="flex flex-row gap-1 mb-3 text-sm border-b border-gray-300">
      <button
        v-for="tab in visibleTabs"
        :key="tab.key"
        class="px-3 py-1.5 -mb-px border-b-2"
        :class="activeTab === tab.key
          ? 'border-accent-primary text-accent-primary font-semibold'
          : 'border-transparent text-gray-500 hover:text-dark-primary'"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
        <span class="ml-1 text-xs text-gray-400">{{ counts[tab.key] }}</span>
      </button>
    </div>

    <!-- Bulk action bar — pending tab: approve/reject -->
    <div
      v-if="activeTab === 'pending' && selectedIds.length > 0"
      class="flex items-center gap-2 mb-3 p-2 rounded bg-gray-100 border border-gray-300 text-sm"
    >
      <span>{{ selectedIds.length }} selected</span>
      <button
        class="px-2 py-1 rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
        :disabled="bulkWorking"
        @click="bulkApprove"
      >{{ bulkWorking ? 'Working...' : 'Approve Selected' }}</button>
      <button
        class="px-2 py-1 rounded border border-red-500 text-red-700 hover:bg-red-50 disabled:opacity-50"
        :disabled="bulkWorking"
        @click="rejectModalOpen = true"
      >Reject Selected</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="openTagsModal('add')"
      >Add Tags</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="openTagsModal('remove')"
      >Remove Tags</button>
      <button
        class="ml-auto text-xs text-gray-500 hover:underline"
        @click="clearSelection"
      >Clear</button>
    </div>

    <!-- Bulk action bar — one button per workflow tab -->
    <div
      v-if="activeTab !== 'pending' && activeTab !== 'closed' && selectedIds.length > 0"
      class="flex items-center gap-2 mb-3 p-2 rounded bg-gray-100 border border-gray-300 text-sm"
    >
      <span>{{ selectedIds.length }} selected</span>
      <button
        v-if="lanePrimaryAction"
        class="px-2 py-1 rounded bg-accent-primary text-white hover:bg-accent-secondary"
        @click="lanePrimaryAction.handler"
      >{{ lanePrimaryAction.label }}</button>
      <button
        v-if="activeTab === 'invoicing'"
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white disabled:opacity-50"
        :disabled="exportingPackage"
        @click="exportInvoicingPackage"
      >{{ exportingPackage ? 'Building...' : 'Export Invoicing Package' }}</button>
      <button
        v-if="activeTab === 'payroll'"
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="downloadPayrollPdf"
      >Download PDF (Gusto Cross-Ref)</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="openTagsModal('add')"
      >Add Tags</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="openTagsModal('remove')"
      >Remove Tags</button>
      <button
        class="ml-auto text-xs text-gray-500 hover:underline"
        @click="clearSelection"
      >Clear</button>
    </div>

    <!-- Bulk action bar — closed tab: only Set Group Label is meaningful -->
    <div
      v-if="activeTab === 'closed' && selectedIds.length > 0"
      class="flex items-center gap-2 mb-3 p-2 rounded bg-gray-100 border border-gray-300 text-sm"
    >
      <span>{{ selectedIds.length }} selected</span>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="openTagsModal('add')"
      >Add Tags</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="openTagsModal('remove')"
      >Remove Tags</button>
      <button
        class="ml-auto text-xs text-gray-500 hover:underline"
        @click="clearSelection"
      >Clear</button>
    </div>

    <div v-if="bulkErrors.length > 0" class="mb-3 p-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
      Some records could not be processed:
      <ul class="list-disc ml-5 mt-1">
        <li v-for="e in bulkErrors" :key="e.id">#{{ e.id }}: {{ e.detail }}</li>
      </ul>
    </div>

    <!-- Filters -->
    <div class="flex flex-wrap gap-3 mb-3 items-end text-sm">
      <div class="flex-1 min-w-[14rem]">
        <label class="block text-xs text-gray-500 mb-0.5">Search</label>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Search description, submitter, client, engagement..."
          class="w-full h-9 px-2 border border-gray-400 rounded-md"
        />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Submitter</label>
        <select v-model="filters.submitter_id" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option v-for="u in submitterOptions" :key="u.id" :value="u.id">{{ fullName(u) }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Exclude submitter</label>
        <select v-model="filters.exclude_submitter_id" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">None</option>
          <option v-for="u in submitterOptions" :key="u.id" :value="u.id">{{ fullName(u) }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Category</label>
        <select v-model="filters.category_id" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option v-for="c in categoryOptions" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Client</label>
        <select v-model="filters.client_name" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option v-for="name in clientOptions" :key="name" :value="name">{{ name }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Engagement</label>
        <select v-model="filters.engagement_title" class="h-9 px-2 border border-gray-400 rounded-md w-56">
          <option :value="null">All</option>
          <option v-for="title in engagementOptions" :key="title" :value="title">{{ title }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Retro</label>
        <select v-model="filters.is_retro" class="h-9 px-2 border border-gray-400 rounded-md w-32">
          <option :value="null">All</option>
          <option :value="true">Retro only</option>
          <option :value="false">Non-retro</option>
        </select>
      </div>
      <div class="min-w-[16rem]">
        <label class="block text-xs text-gray-500 mb-0.5 flex items-center gap-2">
          <span>Tags</span>
          <span v-if="filters.tags.length > 1" class="ml-auto inline-flex rounded border border-gray-300 overflow-hidden text-[10px]">
            <button type="button" :class="['px-1.5 py-0.5', filters.tagCombinator === 'or' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.tagCombinator = 'or'">OR</button>
            <button type="button" :class="['px-1.5 py-0.5 border-l border-gray-300', filters.tagCombinator === 'and' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.tagCombinator = 'and'">AND</button>
          </span>
        </label>
        <TagPicker v-model="filters.tags" placeholder="Any" />
      </div>
      <div class="min-w-[16rem]">
        <label class="block text-xs text-gray-500 mb-0.5 flex items-center gap-2">
          <span>Exclude tags</span>
          <span v-if="filters.exclude_tags.length > 1" class="ml-auto inline-flex rounded border border-gray-300 overflow-hidden text-[10px]">
            <button type="button" :class="['px-1.5 py-0.5', filters.excludeTagCombinator === 'or' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.excludeTagCombinator = 'or'">OR</button>
            <button type="button" :class="['px-1.5 py-0.5 border-l border-gray-300', filters.excludeTagCombinator === 'and' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.excludeTagCombinator = 'and'">AND</button>
          </span>
        </label>
        <TagPicker v-model="filters.exclude_tags" placeholder="None" />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">From</label>
        <input v-model="filters.date_from" type="date" class="h-9 px-2 border border-gray-400 rounded-md w-36" />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">To</label>
        <input v-model="filters.date_to" type="date" class="h-9 px-2 border border-gray-400 rounded-md w-36" />
      </div>
      <button
        v-if="hasActiveFilters"
        class="px-2 py-1.5 text-xs border border-gray-400 rounded hover:bg-gray-100"
        @click="clearFilters"
      >Clear filters</button>
    </div>

    <!-- Filtered total + payroll-only grouping toggle -->
    <div class="flex flex-row items-center justify-between mb-3 text-sm text-gray-600">
      <div v-if="activeTab === 'payroll'" class="flex items-center gap-2">
        <label class="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" v-model="groupBySubmitter" class="cursor-pointer" />
          <span>Group by submitter</span>
        </label>
      </div>
      <div v-else></div>
      <div>
        Filtered total
        <span class="font-semibold text-dark-primary">${{ formatTotal(filteredTotal) }}</span>
        <span class="text-gray-400 mx-1">·</span>
        {{ visibleExpenses.length }} row<span v-if="visibleExpenses.length !== 1">s</span>
        <template v-if="selectedIds.length">
          <span class="text-gray-400 mx-1">·</span>
          <span class="font-semibold text-accent-primary">{{ selectedIds.length }} selected</span>
        </template>
      </div>
    </div>
    </div>

    <!-- Table -->
    <div class="border border-gray-400 rounded-md">
      <table class="table-auto w-full text-sm">
        <thead class="bg-dark-primary text-white sticky z-20" :style="{ top: (topStickyHeight + 56) + 'px' }">
          <tr>
            <th class="px-3 py-2 w-8">
              <input
                type="checkbox"
                :checked="allSelected"
                @change="toggleAllSelected"
              />
            </th>
            <th class="text-left px-3 py-2 w-10"></th>
            <th class="text-left px-3 py-2 w-16">ID</th>
            <th class="text-left px-3 py-2">Submitter</th>
            <th class="text-left px-3 py-2">Date</th>
            <th class="text-left px-3 py-2">Category</th>
            <th class="text-left px-3 py-2">Description</th>
            <th class="text-right px-3 py-2">Amount</th>
            <th class="text-left px-3 py-2">Status</th>
            <th class="text-left px-3 py-2">Client / Engagement</th>
            <th class="text-left px-3 py-2">Reimbursed</th>
            <th class="text-left px-3 py-2">Invoiced</th>
            <th class="text-left px-3 py-2">Client Paid</th>
            <th class="text-left px-3 py-2">Bank Recon.</th>
            <th class="text-center px-3 py-2">Receipts</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="15" class="px-3 py-4 text-center text-gray-500">Loading...</td>
          </tr>
          <tr v-else-if="visibleExpenses.length === 0">
            <td colspan="15" class="px-3 py-4 text-center text-gray-500">No expenses in this view.</td>
          </tr>
          <template v-for="row in displayRows" :key="row.kind === 'group' ? `g-${row.key}` : row.exp.id">
            <tr
              v-if="row.kind === 'group'"
              class="bg-amber-50 border-t border-amber-200 cursor-pointer hover:bg-amber-100"
              @click="toggleGroupCollapsed(row.key)"
            >
              <td class="px-3 py-2" @click.stop>
                <input
                  type="checkbox"
                  :checked="groupAllSelected(row)"
                  @change="toggleGroup(row)"
                />
              </td>
              <td colspan="14" class="px-3 py-2 text-sm">
                <span class="text-gray-500 mr-2 text-xs">{{ isGroupCollapsed(row.key) ? '▶' : '▼' }}</span>
                <template v-if="row.groupKind === 'submitter'">
                  <span class="font-semibold text-dark-primary">{{ row.submitter_name }}</span>
                </template>
                <template v-else>
                  <span class="font-semibold text-dark-primary">{{ row.client_name || '—' }}</span>
                  <span v-if="row.engagement_title"> — {{ row.engagement_title }}</span>
                </template>
                <span class="ml-3 text-base font-medium text-gray-800">
                  {{ row.count }} record<span v-if="row.count !== 1">s</span>,
                  total ${{ formatAmount(row.total) }}
                </span>
                <button
                  type="button"
                  class="ml-2 inline-flex items-center justify-center h-8 w-8 rounded border border-gray-300 bg-white hover:bg-gray-100 align-middle"
                  title="Copy total amount as plain number"
                  @click.stop="copyGroupTotal(row)"
                >
                  <DocumentDuplicateIcon class="h-5 w-5 text-gray-600" />
                  <span class="sr-only">Copy total</span>
                </button>
                <span class="ml-3 text-base font-medium text-gray-800">Expense #s</span>
                <button
                  type="button"
                  class="ml-2 inline-flex items-center justify-center h-8 w-8 rounded border border-gray-300 bg-white hover:bg-gray-100 align-middle"
                  title="Copy expense IDs (e.g. for Gusto reference)"
                  @click.stop="copyGroupExpenseIds(row)"
                >
                  <DocumentDuplicateIcon class="h-5 w-5 text-gray-600" />
                  <span class="sr-only">Copy expense IDs</span>
                </button>
              </td>
            </tr>
            <tr v-else-if="!isGroupCollapsed(row.groupKey)" class="border-t border-gray-200 hover:bg-gray-50 cursor-pointer" @click="toggleExpand(row.exp.id)">
              <td class="px-3 py-2" @click.stop>
                <input
                  type="checkbox"
                  :value="row.exp.id"
                  v-model="selectedIds"
                />
              </td>
              <td class="px-3 py-2 text-gray-400">
                {{ expandedId === row.exp.id ? '▼' : '▶' }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-gray-500">#{{ row.exp.id }}</td>
              <td class="px-3 py-2 whitespace-nowrap">{{ fullName(row.exp.submitter) }}</td>
              <td class="px-3 py-2 whitespace-nowrap">{{ formatDate(row.exp.date_of_expense) }}</td>
              <td class="px-3 py-2 whitespace-nowrap">{{ row.exp.category ? row.exp.category.name : '—' }}</td>
              <td class="px-3 py-2">
                {{ row.exp.description }}
                <div v-if="row.exp.tags && row.exp.tags.length" class="mt-1 flex flex-wrap gap-1">
                  <span
                    v-for="tag in row.exp.tags"
                    :key="tag.id"
                    @click.stop="addTagToFilter(tag)"
                    title="Click to filter by this tag"
                    class="inline-block px-1.5 py-0.5 text-xs rounded bg-accent-primary text-white cursor-pointer hover:bg-accent-secondary"
                  >{{ tag.name }}</span>
                </div>
              </td>
              <td class="px-3 py-2 text-right whitespace-nowrap">
                <span
                  v-if="row.exp.payment_method === 'company_paid'"
                  class="mr-1 px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700"
                  title="Paid via company card"
                >co.</span>
                ${{ formatAmount(row.exp.amount) }}
              </td>
              <td class="px-3 py-2">
                <StatusPill :status="row.exp.status" />
                <div v-if="row.exp.is_retro" class="mt-1">
                  <span class="inline-block px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">Retro</span>
                </div>
              </td>
              <td class="px-3 py-2">
                <template v-if="row.exp.is_client_reimbursable">
                  <span>{{ row.exp.client_name || '—' }}</span>
                  <span v-if="row.exp.engagement_title"> — {{ row.exp.engagement_title }}</span>
                </template>
                <span v-else class="text-gray-400">not client reimbursable</span>
              </td>
              <td class="px-3 py-2 text-center whitespace-nowrap">
                <template v-if="row.exp.payment_method === 'personal_paid'">
                  <span v-if="row.exp.submitter_reimbursed" class="text-green-700">{{ formatDate(row.exp.submitter_reimbursed_at) }}</span>
                  <span v-else class="text-gray-400">—</span>
                </template>
                <span v-else class="text-gray-300">n/a</span>
              </td>
              <td class="px-3 py-2 text-center">
                <template v-if="row.exp.is_client_reimbursable">
                  <span v-if="row.exp.client_invoiced" class="text-green-700">✓</span>
                  <span v-else class="text-gray-400">—</span>
                </template>
                <span v-else class="text-gray-300">n/a</span>
              </td>
              <td class="px-3 py-2 text-center">
                <template v-if="row.exp.is_client_reimbursable">
                  <span v-if="row.exp.client_paid" class="text-green-700">✓</span>
                  <span v-else class="text-gray-400">—</span>
                </template>
                <span v-else class="text-gray-300">n/a</span>
              </td>
              <td class="px-3 py-2 text-center">
                <template v-if="row.exp.payment_method === 'company_paid'">
                  <span v-if="row.exp.bank_reconciled" class="text-green-700">✓</span>
                  <span v-else class="text-gray-400">—</span>
                </template>
                <span v-else class="text-gray-300">n/a</span>
              </td>
              <td class="px-3 py-2 text-center">{{ row.exp.receipt_count || 0 }}</td>
            </tr>
            <tr v-if="row.kind === 'expense' && expandedId === row.exp.id" class="bg-gray-50 border-t border-gray-200">
              <td></td><td></td>
              <td colspan="12" class="px-3 py-3">
                <ExpenseReviewPanel
                  :expenseId="row.exp.id"
                  :detail="detailCache[row.exp.id]"
                  :loading="detailLoading[row.exp.id]"
                  :allowAccountingActions="activeTab === 'pending' && row.exp.status === 'manager_approved'"
                  @approved="onChanged"
                  @rejected="onChanged"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <BulkBatchModal
      v-if="bulkOpen"
      :batchType="bulkType"
      :recordIds="selectedIds"
      :records="selectedRecords"
      @close="bulkOpen = false"
      @applied="onBulkApplied"
    />

    <BulkRejectModal
      v-if="rejectModalOpen"
      :count="selectedIds.length"
      :submitting="bulkWorking"
      :error="rejectModalError"
      @close="rejectModalOpen = false"
      @confirm="confirmBulkReject"
    />

    <MarkPaidModal
      v-if="markPaidOpen"
      :recordIds="selectedIds"
      :records="selectedRecords"
      @close="markPaidOpen = false"
      @applied="onMarkPaidApplied"
    />

    <TagsBulkModal
      v-if="tagsModalOpen"
      :recordIds="selectedIds"
      :mode="tagsModalMode"
      @close="tagsModalOpen = false"
      @applied="onTagsApplied"
    />
  </div>
</template>

<script>
export default { name: "accounting-queue" };
</script>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import {
  getExpenses,
  getExpense,
  bulkAccountingApprove,
  bulkAccountingReject,
  downloadInvoicingPackage,
  getExpenseGroupLabels,
} from "@/services/Expense.js";
import StatusPill from "./StatusPill.vue";
import ExpenseReviewPanel from "./ExpenseReviewPanel.vue";
import BulkBatchModal from "./BulkBatchModal.vue";
import BulkRejectModal from "./BulkRejectModal.vue";
import MarkPaidModal from "./MarkPaidModal.vue";
import TagsBulkModal from "./TagsBulkModal.vue";
import TagPicker from "./TagPicker.vue";
import { DocumentDuplicateIcon } from "@heroicons/vue/20/solid";
import { useStickyOffset } from "@/composables/useStickyOffset";

const store = useStore();
const $toast = useToast();

const loading = ref(false);
const activeTab = ref("pending");
const groupBySubmitter = ref(false);
const topStickyRef = ref(null);
const topStickyHeight = useStickyOffset(topStickyRef);
const expenses = ref([]);
// Tracks which group sections are collapsed (per group key). A
// missing entry means expanded; entries that match the current
// grouping mode hide the corresponding expense rows.
const collapsedGroupKeys = ref(new Set());

function toggleGroupCollapsed(key) {
  const next = new Set(collapsedGroupKeys.value);
  if (next.has(key)) next.delete(key); else next.add(key);
  collapsedGroupKeys.value = next;
}

function isGroupCollapsed(key) {
  return collapsedGroupKeys.value.has(key);
}
const expandedId = ref(null);
const detailCache = reactive({});
const detailLoading = reactive({});
const selectedIds = ref([]);
const bulkOpen = ref(false);
const bulkType = ref("payroll");
const bulkWorking = ref(false);
const rejectModalOpen = ref(false);
const rejectModalError = ref("");
const bulkErrors = ref([]);
const markPaidOpen = ref(false);
const exportingPackage = ref(false);
const tagsModalOpen = ref(false);
const tagsModalMode = ref("add");
const groupLabelOptions = ref([]);

const tabs = [
  { key: "pending", label: "Pending My Review", requires: 'accounting' },
  { key: "payroll", label: "Pending Reimbursement (Payroll)", requires: 'accounting' },
  { key: "invoicing", label: "Pending Invoicing", requires: 'invoicing' },
  { key: "invoiced", label: "Invoiced (Awaiting Payment)", requires: 'invoicing' },
  { key: "bank_recon", label: "Pending Bank Recon", requires: 'accounting' },
  { key: "closed", label: "Closed", requires: 'accounting' },
];

const userRoles = computed(() => {
  const p = store.state.auth.userPermissions || {};
  return {
    accounting: !!p.canAccessExpenseAccounting,
    invoicing: !!(p.canAccessExpenseAccounting || p.isExpenseInvoicing),
  };
});

const visibleTabs = computed(() => {
  const r = userRoles.value;
  return tabs.filter((t) => r[t.requires]);
});

const filters = reactive({
  search: "",
  submitter_id: null,
  exclude_submitter_id: null,
  category_id: null,
  client_name: null,
  group_label: "",
  is_retro: null,
  tags: [],
  tagCombinator: "or",
  exclude_tags: [],
  excludeTagCombinator: "or",
  date_from: null,
  date_to: null,
});

const submitterOptions = computed(() => {
  const map = new Map();
  for (const e of expenses.value) {
    if (e.submitter && !map.has(e.submitter.id)) {
      map.set(e.submitter.id, e.submitter);
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const an = (`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.username || '').toLowerCase();
    const bn = (`${b.first_name || ''} ${b.last_name || ''}`.trim() || b.username || '').toLowerCase();
    return an.localeCompare(bn);
  });
});

const categoryOptions = computed(() => {
  const map = new Map();
  for (const e of expenses.value) {
    if (e.category && !map.has(e.category.id)) {
      map.set(e.category.id, e.category);
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const ao = a.display_order ?? 0;
    const bo = b.display_order ?? 0;
    if (ao !== bo) return ao - bo;
    return (a.name || '').localeCompare(b.name || '');
  });
});

const clientOptions = computed(() => {
  const set = new Set();
  for (const e of expenses.value) {
    if (e.is_client_reimbursable && e.client_name) set.add(e.client_name);
  }
  return Array.from(set).sort();
});

const groupLabelFilterOptions = computed(() => {
  const set = new Set();
  for (const e of expenses.value) {
    if (e.group_label) set.add(e.group_label);
  }
  return Array.from(set).sort();
});

const hasActiveFilters = computed(() => !!(
  filters.search ||
  filters.submitter_id ||
  filters.exclude_submitter_id ||
  filters.category_id ||
  filters.client_name ||
  filters.group_label ||
  filters.is_retro !== null ||
  (filters.tags && filters.tags.length) ||
  (filters.exclude_tags && filters.exclude_tags.length) ||
  filters.date_from ||
  filters.date_to
));

function addTagToFilter(tag) {
  if (!tag) return;
  const idMatch = tag.id != null && filters.tags.some((t) => t.id === tag.id);
  const nameMatch = filters.tags.some((t) => (t.name || '').toLowerCase() === (tag.name || '').toLowerCase());
  if (idMatch || nameMatch) return;
  filters.tags.push({ id: tag.id, name: tag.name });
}

function clearFilters() {
  filters.search = "";
  filters.submitter_id = null;
  filters.exclude_submitter_id = null;
  filters.category_id = null;
  filters.client_name = null;
  filters.group_label = "";
  filters.is_retro = null;
  filters.tags = [];
  filters.tagCombinator = "or";
  filters.exclude_tags = [];
  filters.excludeTagCombinator = "or";
  filters.date_from = null;
  filters.date_to = null;
}

function passesFilters(e) {
  if (filters.search) {
    const q = filters.search.trim().toLowerCase();
    if (q) {
      const submitterText = e.submitter
        ? `${e.submitter.first_name || ''} ${e.submitter.last_name || ''} ${e.submitter.username || ''}`
        : '';
      const haystack = [
        e.description,
        e.category ? e.category.name : '',
        e.client_name,
        e.engagement_title,
        submitterText,
        e.group_label,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
  }
  if (filters.submitter_id && (!e.submitter || e.submitter.id !== filters.submitter_id)) return false;
  if (filters.exclude_submitter_id && e.submitter && e.submitter.id === filters.exclude_submitter_id) return false;
  if (filters.category_id && (!e.category || e.category.id !== filters.category_id)) return false;
  if (filters.client_name && e.client_name !== filters.client_name) return false;
  if (filters.group_label) {
    const wanted = filters.group_label.trim().toLowerCase();
    if (wanted && (e.group_label || '').toLowerCase() !== wanted) return false;
  }
  if (filters.is_retro !== null && !!e.is_retro !== filters.is_retro) return false;
  if (filters.tags && filters.tags.length) {
    const wantedIds = new Set(filters.tags.map((t) => t.id).filter((x) => x != null));
    const wantedNames = new Set(filters.tags.map((t) => (t.name || '').toLowerCase()).filter(Boolean));
    const has = (tag) => wantedIds.has(tag.id) || wantedNames.has((tag.name || '').toLowerCase());
    const expTags = e.tags || [];
    if (filters.tagCombinator === 'and') {
      const expIds = new Set(expTags.map((t) => t.id));
      const expNames = new Set(expTags.map((t) => (t.name || '').toLowerCase()));
      const allMatch = filters.tags.every((t) => (t.id != null && expIds.has(t.id)) || expNames.has((t.name || '').toLowerCase()));
      if (!allMatch) return false;
    } else {
      if (!expTags.some(has)) return false;
    }
  }
  if (filters.exclude_tags && filters.exclude_tags.length) {
    const excludeIds = new Set(filters.exclude_tags.map((t) => t.id).filter((x) => x != null));
    const excludeNames = new Set(filters.exclude_tags.map((t) => (t.name || '').toLowerCase()).filter(Boolean));
    const expTags = e.tags || [];
    const has = (tag) => excludeIds.has(tag.id) || excludeNames.has((tag.name || '').toLowerCase());
    if (filters.excludeTagCombinator === 'and') {
      // Hide only rows that carry ALL of the excluded tags.
      const expIds = new Set(expTags.map((t) => t.id));
      const expNames = new Set(expTags.map((t) => (t.name || '').toLowerCase()));
      const hasAll = filters.exclude_tags.every((t) => (t.id != null && expIds.has(t.id)) || expNames.has((t.name || '').toLowerCase()));
      if (hasAll) return false;
    } else if (expTags.some(has)) {
      return false;
    }
  }
  if (filters.date_from && (!e.date_of_expense || e.date_of_expense < filters.date_from)) return false;
  if (filters.date_to && (!e.date_of_expense || e.date_of_expense > filters.date_to)) return false;
  return true;
}

function inOpenAccountingState(e) {
  return e.status === 'accounting_approved' || e.status === 'closed';
}

function expenseInTab(e, key) {
  switch (key) {
    case 'pending':
      return e.status === 'manager_approved';
    case 'payroll':
      return inOpenAccountingState(e)
        && e.payment_method === 'personal_paid'
        && !e.submitter_reimbursed;
    case 'invoicing':
      return inOpenAccountingState(e)
        && e.is_client_reimbursable
        && !e.client_invoiced;
    case 'invoiced':
      return inOpenAccountingState(e)
        && e.is_client_reimbursable
        && e.client_invoiced
        && !e.client_paid;
    case 'bank_recon':
      return inOpenAccountingState(e)
        && e.payment_method === 'company_paid'
        && !e.bank_reconciled;
    case 'closed':
      return e.status === 'closed';
  }
  return false;
}

function fullName(u) {
  if (!u) return "—";
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return name || u.username;
}

function formatDate(iso) {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
  return d.toLocaleDateString();
}

function formatAmount(amount) {
  if (amount === null || amount === undefined) return "0.00";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return num.toFixed(2);
}

async function copyToClipboard(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    $toast.open({ message: `${label} copied`, type: "success", duration: 1500 });
  } catch (e) {
    $toast.open({ message: "Copy failed", type: "error", duration: 2000 });
  }
}

function copyGroupExpenseIds(group) {
  if (!group || !group.ids || group.ids.length === 0) return;
  copyToClipboard(`Expense # ${group.ids.join(', ')}`, "Expense IDs");
}

function copyGroupTotal(group) {
  copyToClipboard(formatAmount(group.total), "Total");
}

function formatTotal(n) {
  return (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const filteredTotal = computed(() =>
  visibleExpenses.value.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
);

const visibleExpenses = computed(() => {
  const list = expenses.value.filter(
    (e) => expenseInTab(e, activeTab.value) && passesFilters(e),
  );
  if (activeTab.value === 'invoicing') {
    return [...list].sort((a, b) => {
      const ae = (a.engagement_title || '').toLowerCase();
      const be = (b.engagement_title || '').toLowerCase();
      if (ae !== be) return ae < be ? -1 : 1;
      return (a.date_of_expense || '') < (b.date_of_expense || '') ? -1 : 1;
    });
  }
  if (activeTab.value === 'payroll' && groupBySubmitter.value) {
    return [...list].sort((a, b) => {
      const an = fullName(a.submitter).toLowerCase();
      const bn = fullName(b.submitter).toLowerCase();
      if (an !== bn) return an < bn ? -1 : 1;
      return (a.date_of_expense || '') < (b.date_of_expense || '') ? -1 : 1;
    });
  }
  return list;
});

// On the invoicing tab, decorate the list with group-header rows so the
// table reads as a per-engagement grouping. The payroll tab supports an
// opt-in per-submitter grouping via the toggle above the table. Outside
// those cases we emit plain expense rows.
const displayRows = computed(() => {
  const invoicingGrouping = activeTab.value === 'invoicing';
  const submitterGrouping = activeTab.value === 'payroll' && groupBySubmitter.value;
  if (!invoicingGrouping && !submitterGrouping) {
    return visibleExpenses.value.map((exp) => ({ kind: 'expense', exp }));
  }
  const rows = [];
  let lastKey = Symbol('init');
  let runningGroup = null;
  for (const exp of visibleExpenses.value) {
    let key;
    let header;
    if (submitterGrouping) {
      const sid = exp.submitter ? exp.submitter.id : '__none__';
      key = `submitter|${sid}`;
      header = {
        kind: 'group',
        key,
        groupKind: 'submitter',
        submitter_name: fullName(exp.submitter) || '(no submitter)',
        count: 0,
        total: 0,
        ids: [],
      };
    } else {
      key = (exp.engagement_title || '__none__') + '|' + (exp.client_name || '');
      header = {
        kind: 'group',
        key,
        groupKind: 'engagement',
        engagement_title: exp.engagement_title || '(no engagement)',
        client_name: exp.client_name || '',
        count: 0,
        total: 0,
        ids: [],
      };
    }
    if (key !== lastKey) {
      runningGroup = header;
      rows.push(runningGroup);
      lastKey = key;
    }
    runningGroup.count += 1;
    runningGroup.total += parseFloat(exp.amount) || 0;
    runningGroup.ids.push(exp.id);
    rows.push({ kind: 'expense', exp, groupKey: key });
  }
  return rows;
});

function groupAllSelected(group) {
  return group.ids.length > 0 && group.ids.every((id) => selectedIds.value.includes(id));
}

function toggleGroup(group) {
  const set = new Set(group.ids);
  if (groupAllSelected(group)) {
    selectedIds.value = selectedIds.value.filter((id) => !set.has(id));
  } else {
    selectedIds.value = Array.from(new Set([...selectedIds.value, ...group.ids]));
  }
}

const counts = computed(() => {
  const out = {};
  for (const tab of tabs) {
    out[tab.key] = expenses.value.filter(
      (e) => expenseInTab(e, tab.key) && passesFilters(e),
    ).length;
  }
  return out;
});

const selectedRecords = computed(() =>
  expenses.value.filter((e) => selectedIds.value.includes(e.id))
);

const allSelected = computed(() =>
  visibleExpenses.value.length > 0 &&
  visibleExpenses.value.every((e) => selectedIds.value.includes(e.id))
);

function toggleAllSelected() {
  if (allSelected.value) {
    selectedIds.value = selectedIds.value.filter(
      (id) => !visibleExpenses.value.find((e) => e.id === id)
    );
  } else {
    const toAdd = visibleExpenses.value.map((e) => e.id);
    selectedIds.value = Array.from(new Set([...selectedIds.value, ...toAdd]));
  }
}

function clearSelection() {
  selectedIds.value = [];
}

async function toggleExpand(id) {
  if (expandedId.value === id) {
    expandedId.value = null;
    return;
  }
  expandedId.value = id;
  if (!detailCache[id]) {
    detailLoading[id] = true;
    const token = store.state.auth.token;
    try {
      detailCache[id] = await getExpense(token, id);
    } catch (error) {
      $toast.open({ message: "Failed to load detail", type: "error", duration: 3000 });
    }
    detailLoading[id] = false;
  }
}

async function loadExpenses() {
  loading.value = true;
  const token = store.state.auth.token;
  try {
    const data = await getExpenses(token, "?status__in=manager_approved,accounting_approved,closed&page_size=1000");
    expenses.value = Array.isArray(data) ? data : (data.results || []);
  } catch (error) {
    $toast.open({ message: "Failed to load accounting queue", type: "error", duration: 3000 });
  }
  loading.value = false;
}

function openBulk(type) {
  bulkType.value = type;
  bulkOpen.value = true;
}

const lanePrimaryAction = computed(() => {
  switch (activeTab.value) {
    case 'payroll':
      return { label: 'Mark as Reimbursed', handler: () => openBulk('payroll') };
    case 'invoicing':
      return { label: 'Mark as Invoiced', handler: () => openBulk('client_invoice') };
    case 'invoiced':
      // Marking client paid is an accounting action — invoicing-only
      // users see the Invoiced tab as read-only (for context on what
      // they've already invoiced) but cannot record payment receipt.
      if (!userRoles.value.accounting) return null;
      return { label: 'Mark as Paid', handler: () => { markPaidOpen.value = true; } };
    case 'bank_recon':
      return { label: 'Mark Bank Reconciled', handler: () => openBulk('bank_reconciliation') };
    default:
      return null;
  }
});

async function bulkApprove() {
  if (selectedIds.value.length === 0) return;
  bulkWorking.value = true;
  bulkErrors.value = [];
  const token = store.state.auth.token;
  try {
    const result = await bulkAccountingApprove(token, [...selectedIds.value]);
    const applied = (result.applied_ids || []).length;
    bulkErrors.value = result.errors || [];
    if (applied > 0) {
      $toast.open({
        message: `Approved ${applied} record${applied === 1 ? '' : 's'}${bulkErrors.value.length ? ' — see errors below' : ''}`,
        type: bulkErrors.value.length ? 'warning' : 'success',
        duration: 2500,
      });
    } else {
      $toast.open({ message: 'No records approved', type: 'error', duration: 2500 });
    }
    selectedIds.value = [];
    await loadExpenses();
  } catch (error) {
    $toast.open({ message: 'Bulk approve failed', type: 'error', duration: 3000 });
  }
  bulkWorking.value = false;
}

async function confirmBulkReject(reason) {
  if (selectedIds.value.length === 0) return;
  bulkWorking.value = true;
  rejectModalError.value = "";
  bulkErrors.value = [];
  const token = store.state.auth.token;
  try {
    const result = await bulkAccountingReject(token, [...selectedIds.value], reason);
    const applied = (result.applied_ids || []).length;
    bulkErrors.value = result.errors || [];
    if (applied > 0) {
      $toast.open({
        message: `Rejected ${applied} record${applied === 1 ? '' : 's'}${bulkErrors.value.length ? ' — see errors below' : ''}`,
        type: bulkErrors.value.length ? 'warning' : 'success',
        duration: 2500,
      });
    } else {
      rejectModalError.value = 'No records rejected.';
    }
    selectedIds.value = [];
    rejectModalOpen.value = false;
    await loadExpenses();
  } catch (error) {
    rejectModalError.value = 'Bulk reject failed';
  }
  bulkWorking.value = false;
}

async function onChanged(updated) {
  detailCache[updated.id] = updated;
  await loadExpenses();
}

async function onBulkApplied() {
  bulkOpen.value = false;
  selectedIds.value = [];
  await loadExpenses();
}

async function onMarkPaidApplied() {
  markPaidOpen.value = false;
  selectedIds.value = [];
  await loadExpenses();
}

async function loadGroupLabels() {
  const token = store.state.auth.token;
  try {
    const data = await getExpenseGroupLabels(token);
    groupLabelOptions.value = (data && data.group_labels) || [];
  } catch (error) {
    groupLabelOptions.value = [];
  }
}

function openTagsModal(mode) {
  if (selectedIds.value.length === 0) return;
  tagsModalMode.value = mode || "add";
  tagsModalOpen.value = true;
}

async function onTagsApplied() {
  tagsModalOpen.value = false;
  selectedIds.value = [];
  await loadExpenses();
}

// Mirror the on-screen Payroll-tab table for the selected rows into a
// PDF that an accountant can attach to the corresponding Gusto expense
// row, so reviewers can cross-reference which Aerocle records made up
// the batched line.
async function downloadPayrollPdf() {
  if (selectedIds.value.length === 0) return;
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const rows = expenses.value.filter((e) => selectedIds.value.includes(e.id));
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  const generatedAt = new Date().toLocaleString();
  doc.setFontSize(14);
  doc.text('Aerocle Expenses — Payroll Reimbursement Cross-Reference', 40, 36);
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(`Generated ${generatedAt} · ${rows.length} record(s)`, 40, 50);
  doc.setTextColor(0);
  const total = rows.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  autoTable(doc, {
    startY: 64,
    head: [[
      'ID', 'Submitter', 'Date', 'Category', 'Description', 'Amount',
      'Status', 'Client / Engagement', 'Reimbursed', 'Receipts',
    ]],
    body: rows.map((e) => [
      `#${e.id}`,
      fullName(e.submitter),
      formatDate(e.date_of_expense),
      e.category ? e.category.name : '—',
      e.description,
      `$${formatAmount(e.amount)}`,
      e.status,
      e.is_client_reimbursable
        ? `${e.client_name || '—'}${e.engagement_title ? ' — ' + e.engagement_title : ''}`
        : '—',
      e.submitter_reimbursed
        ? `Yes${e.payroll_batch_reference ? ' (' + e.payroll_batch_reference + ')' : ''}`
        : 'No',
      String(e.receipt_count || 0),
    ]),
    foot: [[
      { content: 'Total', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } },
      { content: `$${formatTotal(total)}`, styles: { fontStyle: 'bold' } },
      '', '', '', '',
    ]],
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [37, 39, 77], textColor: 255 },
    footStyles: { fillColor: [240, 240, 240], textColor: 0 },
    columnStyles: {
      0: { cellWidth: 36 },
      1: { cellWidth: 90 },
      2: { cellWidth: 60 },
      3: { cellWidth: 80 },
      5: { cellWidth: 50, halign: 'right' },
      6: { cellWidth: 70 },
      8: { cellWidth: 70 },
      9: { cellWidth: 50, halign: 'center' },
    },
    margin: { left: 40, right: 40 },
  });
  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`aerocle-payroll-cross-ref-${stamp}.pdf`);
}

async function exportInvoicingPackage() {
  if (selectedIds.value.length === 0) return;
  exportingPackage.value = true;
  const token = store.state.auth.token;
  try {
    const filename = await downloadInvoicingPackage(token, [...selectedIds.value]);
    $toast.open({ message: `Downloaded ${filename}`, type: 'success', duration: 2500 });
  } catch (error) {
    $toast.open({ message: 'Failed to build invoicing package', type: 'error', duration: 3000 });
  }
  exportingPackage.value = false;
}

watch(activeTab, () => {
  selectedIds.value = [];
  bulkErrors.value = [];
});

// Keep the selection consistent with what's actually on screen. Switching
// tabs clears it (above), but changing a filter only changes which rows are
// visible — without this, rows selected before the filter stay in
// selectedIds while hidden, so the invoicing export / bulk actions would
// operate on more than the visible selected rows. Drop any selected id that
// is no longer visible.
watch(visibleExpenses, (rows) => {
  if (!selectedIds.value.length) return;
  const visibleIds = new Set(rows.map((e) => e.id));
  const pruned = selectedIds.value.filter((id) => visibleIds.has(id));
  if (pruned.length !== selectedIds.value.length) {
    selectedIds.value = pruned;
  }
});

onMounted(() => {
  // Snap activeTab to the first tab the user is allowed to see — an
  // invoicing-only user lands on Pending Invoicing rather than the
  // (now-hidden) default Pending My Review.
  if (!visibleTabs.value.find((t) => t.key === activeTab.value)) {
    activeTab.value = visibleTabs.value[0]?.key || 'pending';
  }
  loadExpenses();
  loadGroupLabels();
});
</script>
